import { FormControlLabel, Grid, Typography } from '@mui/material';

import { FCheckbox, FHRadio, FHRadioGroup } from '../../../../../base';
import { FORTA_BLACK } from '../../../../../../utils/colors';
import { getPrimaryParticipantColumnLabel } from './utils/general';

import type { ChangeEvent } from 'react';
import type { IClientSupervision, IProvider } from '../../../../../../utils/types';

interface BehavioralTechnicianSectionProps {
  isBay6: boolean;
  bay6EditModeProvider: string;
  bay6ViewModeProvider: string;
  isEditable: boolean;
  concludedBtParticipants: IClientSupervision['bt_participants'] | null;
  btParticipants: IProvider[];
  secondaryParticipants: IProvider[];
  primaryParticipantId: number | string;
  selectedSecondaryParticipantIds: number[];
  participantSelectionDisabled: boolean;
  showNoActiveBtsMessage: boolean;
  onPrimaryParticipantChange: (providerId: number) => void;
  onSecondaryParticipantChange: (providerId: number, checked: boolean) => void;
}

function BehavioralTechnicianSection(props: BehavioralTechnicianSectionProps) {
  const {
    isBay6,
    bay6EditModeProvider,
    bay6ViewModeProvider,
    isEditable,
    concludedBtParticipants,
    btParticipants,
    secondaryParticipants,
    primaryParticipantId,
    selectedSecondaryParticipantIds,
    participantSelectionDisabled,
    showNoActiveBtsMessage,
    onPrimaryParticipantChange,
    onSecondaryParticipantChange,
  } = props;

  const bay6gridSize = isBay6 ? 8 : 3;

  const handlePrimaryRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    onPrimaryParticipantChange(Number(e.target.value));
  };

  return (
    <>
      {isEditable ? (
        <>
          <Grid item xs={bay6gridSize}>
            {btParticipants.length > 0 ? (
              <>
                <Typography fontWeight="bold" textAlign="left" mb={1}>
                  {getPrimaryParticipantColumnLabel(isBay6, btParticipants.length > 1)}
                </Typography>
                {btParticipants.length > 1 && !isBay6 ? (
                  <FHRadioGroup value={primaryParticipantId} onChange={handlePrimaryRadioChange}>
                    {btParticipants.map((provider: IProvider) => (
                      <FormControlLabel
                        data-testid={`${provider.first_name}_${provider.last_name}_radioButton`}
                        key={provider.id}
                        disabled={participantSelectionDisabled}
                        sx={{
                          width: 'fit-content',
                          textAlign: 'left',
                          '& .MuiFormControlLabel-label.Mui-disabled': {
                            color: FORTA_BLACK,
                          },
                        }}
                        value={provider.id}
                        control={<FHRadio />}
                        label={`${provider.first_name} ${provider.last_name}`}
                      />
                    ))}
                  </FHRadioGroup>
                ) : (
                  <Typography textAlign="left">
                    {isBay6
                      ? bay6EditModeProvider
                      : `${btParticipants[0].first_name} ${btParticipants[0].last_name}`}
                  </Typography>
                )}
              </>
            ) : (
              <Typography fontSize={16} ml={1} textAlign="left">
                {showNoActiveBtsMessage && 'No active BTs assigned for this client'}
              </Typography>
            )}
          </Grid>
          {!isBay6 && (
            <Grid item xs={5} display="flex" flexDirection="column">
              {secondaryParticipants.length > 0 && (
                <>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Behavioral technician(s)
                  </Typography>
                  {secondaryParticipants.map((provider: IProvider) => (
                    <FCheckbox
                      data-testid={`${provider.first_name}_${provider.last_name}_checkBox`}
                      key={provider.id}
                      sx={{ mb: 1 }}
                      iconSize={{ width: 24, height: 24 }}
                      disabled={participantSelectionDisabled}
                      label={`${provider.first_name} ${provider.last_name}`}
                      checked={selectedSecondaryParticipantIds.includes(provider.id)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        onSecondaryParticipantChange(provider.id, e.target.checked)
                      }
                    />
                  ))}
                </>
              )}
            </Grid>
          )}
        </>
      ) : (
        <>
          <Grid item xs={bay6gridSize}>
            {concludedBtParticipants && (
              <>
                <Typography fontWeight="bold" textAlign="left" mb={1}>
                  {getPrimaryParticipantColumnLabel(
                    isBay6,
                    concludedBtParticipants.secondary.length > 0,
                  )}
                </Typography>
                {(isBay6 || concludedBtParticipants.primary) && (
                  <Typography textAlign="left">
                    {isBay6
                      ? bay6ViewModeProvider
                      : concludedBtParticipants.primary
                        ? `${concludedBtParticipants.primary.first_name} ${concludedBtParticipants.primary.last_name}`
                        : ''}
                  </Typography>
                )}
              </>
            )}
          </Grid>
          {!isBay6 && (
            <Grid item xs={5} display="flex" flexDirection="column">
              {concludedBtParticipants && concludedBtParticipants.secondary.length > 0 && (
                <>
                  <Typography fontWeight="bold" textAlign="left" mb={1}>
                    Behavioral technician(s)
                  </Typography>
                  {concludedBtParticipants.secondary.map((provider: IProvider) => (
                    <Typography textAlign="left" mb={1} key={provider.id}>
                      {`${provider.first_name} ${provider.last_name}`}
                    </Typography>
                  ))}
                </>
              )}
            </Grid>
          )}
        </>
      )}
    </>
  );
}

export default BehavioralTechnicianSection;
